/**
 * Quando o cartão da definição matemática aparece (D4, ORD-F-078/082; docs/DECISOES.md §63). O cartão (JS e CSS) fica no
 * JS crítico, sem pedaço próprio: com um `import()` novo o Rolldown parte o crítico num chunk comum, e a apuração no
 * celular perdeu 128 ms de LCP na medida de 9 cargas alternadas (ORD-F-087: mais de 100 ms, o item volta para o `index`).
 *
 * Abre quando, ao mesmo tempo: a chave está ligada; a linha do tempo está no ao vivo; não é a TV (lá vale o
 * `EventOverlay`, §12.4); a vista ao vivo tem a decisão matemática nacional do presidente (`national.mathDecision`) e
 * ainda NÃO tem o resultado do TSE (`national.tseResult`: com ele, o herói diz "Eleito · TSE" e o cartão de "Definido
 * matematicamente" seria um passo atrás, regra 3 do CLAUDE.md); e nenhum outro diálogo (busca, Sobre, seletor) está
 * aberto (o cartão espera ele fechar).
 *
 * Uma vez por decisão por sessão: a decisão (vencedor + hora) fica marcada ao abrir e o cartão não reabre nos snapshots
 * seguintes. "Sessão" é o documento carregado: o §2.4.4 só admite três chaves de `sessionStorage` e a /privacidade as
 * lista, então nada é gravado; recarregar a página mostra o cartão de novo, uma vez. Quem chega depois da decisão (e
 * antes do TSE) também vê o cartão, uma vez. Sem botão para reabrir no herói (não cabe sem mexer no layout dele).
 */
import { useEffect, useState } from 'preact/hooks';
import { ESC_PRIORITY, useEscapeLayer } from '../a11y/escape';
import { ErrorBoundary } from '../features/errors/ErrorBoundary';
import { MathDecisionCard } from '../features/hero/MathDecisionCard';
import type { Decision, ResultsView } from '../model/types';
import { useConfig, useLayout, useTimeline, useUi } from '../state/hooks';
import { liveView } from '../state/store';

export interface MathCardGate {
  /** `features.math_card`. */
  on: boolean;
  /** `timeline.live`: a linha do tempo no ao vivo. */
  live: boolean;
  /** TV ligada ou modo de layout `tv`. */
  tv: boolean;
}

/** Pura. A decisão que o cartão mostraria agora, ou null (regras no topo do arquivo). */
export function mathCardDecision(view: ResultsView | null, gate: MathCardGate): Decision | null {
  if (!gate.on || !gate.live || gate.tv || view === null) return null;
  const n = view.national;
  if (n.tseResult !== null || n.mathDecision === null || n.mathDecision.kind !== 'math') return null;
  return n.mathDecision;
}

/** Identidade de uma decisão: vencedor e hora do 1º snapshot que satisfez a regra (§14.3). */
export const mathDecisionKey = (d: Decision): string => `${d.winner}@${d.at}`;

/** Decisões já mostradas neste documento. */
const shown = new Set<string>();

/** Só para os testes: esquece as decisões mostradas (equivale a recarregar a página). */
export function resetMathCardMemory(): void {
  shown.clear();
}

export function MathCardSlot() {
  const cfg = useConfig();
  const ui = useUi();
  const { mode } = useLayout();
  const { live } = useTimeline();
  const view = liveView.value;
  const tv = ui.tv.on || mode === 'tv';
  const decision = mathCardDecision(view, { on: cfg.features.math_card, live, tv });
  const key = decision ? mathDecisionKey(decision) : null;
  const blocked = ui.searchOpen || ui.aboutOpen !== null || ui.pickerOpen;
  const [open, setOpen] = useState<Decision | null>(null);
  useEffect(() => {
    if (decision === null || key === null || blocked || open !== null || shown.has(key)) return;
    shown.add(key);
    setOpen(decision);
  }, [key, blocked, open]);
  // a TV ligada com o cartão aberto fecha o cartão (na TV vale o overlay dela, §12.4)
  useEffect(() => {
    if (tv && open !== null) setOpen(null);
  }, [tv, open]);
  // o Esc do cartão (cascata única, §5.6), no slot, que decide quando o cartão abre
  useEscapeLayer(ESC_PRIORITY.mathCard, open !== null, () => setOpen(null));
  if (open === null || view === null || tv || !cfg.features.math_card) return null;
  return (
    <ErrorBoundary name="math-card" quiet>
      <MathDecisionCard
        view={view}
        decision={open}
        electionDate={cfg.election_date}
        photos={cfg.features.photos}
        onClose={() => setOpen(null)}
      />
    </ErrorBoundary>
  );
}
